import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { COUNTRIES, CountryTabs } from '../components/country-tabs';
import { TestUserPicker } from '../components/test-user-picker';
import { ApiError } from '../config/api';
import {
  useRenderVideo,
  useVideoTemplates,
} from '../hooks/use-render-video';
import { useVideoRuns } from '../hooks/use-video-runs';
import type { CardTheme, VideoTemplateId } from '../types/api';

function validCountryCode(raw: string | null): string {
  if (!raw) return 'global';
  const upper = raw.toUpperCase();
  const found = COUNTRIES.find(
    (c) => c.code.toUpperCase() === upper || c.code === raw,
  );
  return found?.code ?? 'global';
}

const PLATFORMS = ['instagram'] as const;

/**
 * Manual video render composer — mirrors the carousel composer but for the
 * Remotion video pipeline. v1 has a single template (article-story-9x16,
 * Instagram Reels) so the template / platform UI is intentionally minimal;
 * the layout is ready to accept more templates as they ship.
 */
export function VideoComposerPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCountry = validCountryCode(searchParams.get('country'));

  const [testUserEmail, setTestUserEmail] = useState('');
  const [country, setCountry] = useState('');
  const [category, setCategory] = useState('');
  const [templateId, setTemplateId] = useState<VideoTemplateId>(
    'article-story-9x16',
  );
  const [captionOverride, setCaptionOverride] = useState('');
  const [theme, setTheme] = useState<CardTheme | undefined>(undefined);

  useEffect(() => {
    setTestUserEmail('');
    setCountry(activeCountry === 'global' ? '' : activeCountry);
  }, [activeCountry]);

  function onCountryChange(code: string) {
    const next = new URLSearchParams(searchParams);
    if (code === 'global') next.delete('country');
    else next.set('country', code);
    setSearchParams(next, { replace: true });
  }

  const templates = useVideoTemplates();
  const render = useRenderVideo();
  const recentRuns = useVideoRuns(10);

  const canRender = Boolean(testUserEmail) && !render.isPending;

  function onRender() {
    render.mutate({
      testUserEmail,
      country: country || undefined,
      category: category || undefined,
      templateId,
      platforms: ['instagram'],
      captionOverride: captionOverride.trim() || undefined,
      theme,
    });
  }

  const result = render.data;
  const videoAsset = result?.assets.find((a) => a.kind === 'video');
  const thumbAsset = result?.assets.find((a) => a.kind === 'thumbnail');

  return (
    <div className="composer">
      <aside className="composer-side">
        <section className="panel panel-tabs">
          <div className="panel-label">Country</div>
          <CountryTabs value={activeCountry} onChange={onCountryChange} />
        </section>

        <section className="panel">
          <div className="panel-label">Source</div>
          <label>
            Test user
            <TestUserPicker
              value={testUserEmail}
              onChange={setTestUserEmail}
              country={activeCountry === 'global' ? null : activeCountry}
            />
          </label>
          <label>
            Country override
            <input
              className="input"
              type="text"
              value={country}
              placeholder="(use test user's country)"
              onChange={(e) => setCountry(e.target.value)}
            />
          </label>
          <label>
            Category
            <input
              className="input"
              type="text"
              value={category}
              placeholder="(all categories)"
              onChange={(e) => setCategory(e.target.value)}
            />
          </label>
        </section>

        <section className="panel">
          <div className="panel-label">Template</div>
          <select
            className="select"
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value as VideoTemplateId)}
          >
            {(templates.data ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.label} · {t.width}×{t.height}
              </option>
            ))}
            {templates.isLoading ? (
              <option value="article-story-9x16">Loading…</option>
            ) : null}
          </select>
          <div className="dim small">
            v1 has one template (15 s Reel). More to come.
          </div>
        </section>

        <section className="panel">
          <div className="panel-label">Platforms</div>
          <div className="platforms-row">
            {PLATFORMS.map((p) => (
              <span key={p} className="chip chip-on">
                {p}
              </span>
            ))}
          </div>
          <div className="dim small">v1 publishes to Instagram Reels only.</div>
        </section>

        <section className="panel">
          <div className="panel-label">Theme</div>
          <select
            className="select"
            value={theme ?? ''}
            onChange={(e) =>
              setTheme(
                e.target.value === '' ? undefined : (e.target.value as CardTheme),
              )
            }
          >
            <option value="">Random</option>
            <option value="dark">Dark</option>
            <option value="light">Light</option>
          </select>
        </section>

        <section className="panel">
          <div className="panel-label">Caption override (optional)</div>
          <textarea
            className="input"
            rows={4}
            value={captionOverride}
            onChange={(e) => setCaptionOverride(e.target.value)}
            placeholder="Leave blank to auto-compose from the article + default template."
          />
        </section>

        <button
          className="btn btn-primary"
          disabled={!canRender}
          onClick={onRender}
        >
          {render.isPending ? 'Rendering…' : 'Render video'}
        </button>

        {render.error ? (
          <div className="error" style={{ marginTop: 12 }}>
            {render.error instanceof ApiError
              ? `${render.error.message} — ${JSON.stringify(
                  render.error.body,
                ).slice(0, 200)}`
              : (render.error as Error).message}
          </div>
        ) : null}
      </aside>

      <main className="composer-stage">
        {result ? (
          <section className="panel video-result">
            <header className="video-result-head">
              <div>
                <div className="panel-label">Latest render</div>
                <Link to={`/video/runs/${result.runId}`} className="mono small">
                  {result.runId}
                </Link>
              </div>
              <div className="dim small">
                article <span className="mono">{result.articleId}</span> ·{' '}
                {result.template.width}×{result.template.height} ·{' '}
                {Math.round(
                  (result.template.durationInFrames /
                    result.template.fps) *
                    10,
                ) / 10}
                s
              </div>
            </header>

            {videoAsset ? (
              <video
                className="video-player"
                src={videoAsset.publicUrl}
                poster={thumbAsset?.publicUrl}
                controls
                playsInline
              />
            ) : null}

            <div className="caption-preview">
              <div className="panel-label">Caption</div>
              <pre className="caption-text">{result.caption}</pre>
            </div>
          </section>
        ) : (
          <section className="panel empty-state">
            <h3>No video yet</h3>
            <p className="dim">
              Pick a test user and hit <strong>Render video</strong>. The
              result appears here with an inline player and a Publish button.
            </p>
          </section>
        )}

        <section className="panel">
          <header className="row-between">
            <div className="panel-label">Recent runs</div>
            <Link to="/video/runs" className="btn-link small">
              View all
            </Link>
          </header>
          {recentRuns.isLoading ? (
            <div className="muted">Loading…</div>
          ) : (recentRuns.data?.length ?? 0) === 0 ? (
            <div className="dim small">No video runs yet.</div>
          ) : (
            <ul className="run-list">
              {(recentRuns.data ?? []).map((run) => (
                <li key={run.id}>
                  <Link to={`/video/runs/${run.id}`} className="run-row">
                    <span className={`status status-${run.status}`}>
                      {run.status}
                    </span>
                    <span className="mono small">{run.id.slice(0, 8)}</span>
                    <span className="dim small">
                      {run.template_id} · {run.country ?? 'global'}
                    </span>
                    <span className="dim small">
                      {new Date(run.created_at).toLocaleString()}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}
